"use client";

import { useEffect } from "react";
import { spaceGrotesk, spaceMono } from "./fonts";
import { SparkChip } from "@/components/SparkChip";
// global-error REPLACES the root layout (app/layout.tsx) when it fires, so nothing
// that layout ships — including its stylesheet — comes with it. Import it directly
// or this page renders as unstyled browser-default HTML.
import "./globals.css";

// app/layout.tsx puts these two `.variable` classNames on <html>, which is what makes
// globals.css's `var(--font-space-mono)` / `var(--font-space-grotesk)` resolve. This file
// renders its own <html>, so it has to hang both on it again (the faces are declared once,
// in `./fonts`) — otherwise every mono heading here silently falls back to a system face
// while the rest of the app is right.

/**
 * Root-layout error boundary. Fires when app/layout.tsx itself throws, or when a throw
 * elsewhere has no error.tsx boundary above it to land in. Styled from Space's own tokens
 * (paper ground + dot grid, 3px ink borders, hard offset shadow) so it reads as this app
 * and not as a generic browser failure page.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    void (async () => {
      try {
        const { reportError } = await import("@/lib/flare");
        await reportError(error, { kind: "client", digest: error.digest });
      } catch {
        // The page is already broken; a failed report must not make it worse.
      }
    })();
  }, [error]);

  return (
    <html lang="en" className={`${spaceMono.variable} ${spaceGrotesk.variable}`}>
      <head>
        {/* Next's global-error path bypasses metadata/viewport resolution entirely (its own
            DefaultGlobalError has the same gap), so without this the installed iOS PWA
            renders this page at desktop width — exactly the case this file exists for. */}
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
      </head>
      <body>
        <main className="auth">
          <div className="auth-card">
            <div className="mark">
              <SparkChip />
              <b>SPACE</b>
            </div>
            <h1>Space stopped working</h1>
            <p>Something broke while this page was loading. Trying again usually fixes it.</p>
            {/* .auth-card .btn is already full-width; minHeight is the fleet 44px tap floor,
                which .btn's padding alone does not guarantee. */}
            <button type="button" className="btn go" style={{ minHeight: "44px" }} onClick={reset}>
              Try again
            </button>
          </div>
        </main>
      </body>
    </html>
  );
}
